import { IS_FTNFT_AM_MODE, IS_KIOSK_MODE } from 'constants/app/configs';
import { ReviewStatus } from 'constants/nfts/blockchains';
import { AccountType, IAccountCreatorResponse, INft } from 'interfaces';

export const createNftActions = (
  nft: INft,
  accountCreatorData: IAccountCreatorResponse,
  handleListForSale: (nft: INft) => void,
  handleWithdraw: (nft: INft) => void,
  handleTransferNft: (nft: INft) => void,
  setDeleteId: (id: number) => void,
  handleEditNft: (nft: INft) => void,
  handleMintNft: (nft: INft) => void,
  setRevokeId: (id: number) => void,
  openPrintNftModal: (id: number) => void,
  isForMyNftList?: boolean
) => {
  const isOwner = nft.owned_by.id === accountCreatorData.id;
  const isCreator = nft.created_by.id === accountCreatorData.id;
  const isReviewPending = nft.review_status === ReviewStatus.Pending;
  const isReviewRejected = nft.review_status === ReviewStatus.Rejected;
  const isReviewApproved = nft.review_status === ReviewStatus.Approved;
  const isMintConfirmedOrWithdraw = [
    'mint_confirmed',
    'mint_on_withdraw'
  ].includes(nft.status);
  const isMintOnWithdraw = nft.status === 'mint_on_withdraw';
  const isMintConfirmed = nft.status === 'mint_confirmed';
  const isMintLazy = ['mint_lazy'].includes(nft.status);
  const isMintOnSale = nft.status === 'mint_on_sale';
  const isMintError = nft.status === 'mint_error';
  const isMintPending = ['mint_pending', 'mint_init', 'withdraw_init'].includes(
    nft.status
  );
  const isSelling = Boolean(nft.is_saleable);
  const isCollector = accountCreatorData?.account_type === AccountType.User;
  const isCompany = accountCreatorData?.account_type === AccountType.Company;

  const createAMCreatorActions = () => {
    if (isReviewPending) {
      return { del: () => setDeleteId(nft.id) };
    }
    if (isReviewRejected) {
      return {
        edit: () => handleEditNft(nft),
        del: () => setDeleteId(nft.id)
      };
    }
    if (isReviewApproved) {
      if (isMintOnSale) {
        return {
          sell: () => handleListForSale(nft),
          edit: () => handleEditNft(nft),
          del: () => setDeleteId(nft.id)
        };
      }
      if (isMintLazy) {
        return {
          sell: () => {},
          mint: () => handleMintNft(nft),
          edit: () => handleEditNft(nft),
          del: () => setDeleteId(nft.id)
        };
      }
      if (isMintError || isMintPending) {
        return {
          sell: () => {},
          mint: () => handleMintNft(nft)
        };
      }
      if (isMintConfirmedOrWithdraw) {
        if (isSelling) {
          return {
            revoke: () => setRevokeId(nft.id),
            sell: () => handleListForSale(nft),
            withdraw: () => handleWithdraw(nft),
            transfer: () => handleTransferNft(nft),
            ...(IS_KIOSK_MODE && { print: () => openPrintNftModal(nft.id) })
          };
        } else {
          if (nft.utility_product) {
            return {
              sell: () => handleListForSale(nft),
              edit: () => handleEditNft(nft)
            };
          } else {
            return {
              sell: () => handleListForSale(nft),
              withdraw: () => handleWithdraw(nft),
              transfer: () => handleTransferNft(nft),
              ...(IS_KIOSK_MODE && { print: () => openPrintNftModal(nft.id) })
            };
          }
        }
      }
    }
  };

  const createNonAMCreatorActions = () => {
    if (isSelling) {
      return {
        revoke: () => setRevokeId(nft.id),
        sell: () => handleListForSale(nft),
        withdraw: () => handleWithdraw(nft),
        transfer: () => handleTransferNft(nft),
        ...(IS_KIOSK_MODE && { print: () => openPrintNftModal(nft.id) })
      };
    } else {
      if (isMintError || isMintPending) {
        return {
          sell: () => {},
          mint: () => handleMintNft(nft)
        };
      }
      if (isMintConfirmed) {
        return {
          sell: () => handleListForSale(nft),
          withdraw: () => handleWithdraw(nft),
          transfer: () => handleTransferNft(nft),
          ...(IS_KIOSK_MODE && { print: () => openPrintNftModal(nft.id) })
        };
      } else {
        return {
          sell: () => handleListForSale(nft),
          transfer: () => handleTransferNft(nft),
          edit: () => handleEditNft(nft),
          del: () => setDeleteId(nft.id),
          ...(IS_KIOSK_MODE && { print: () => openPrintNftModal(nft.id) }),
          ...(isMintOnWithdraw && { withdraw: () => handleWithdraw(nft) })
        };
      }
    }
  };

  const createAMNonCreatorActions = () => {
    if (isSelling) {
      return {
        revoke: () => setRevokeId(nft.id),
        sell: () => handleListForSale(nft),
        withdraw: () => handleWithdraw(nft),
        transfer: () => handleTransferNft(nft),
        ...(IS_KIOSK_MODE && { print: () => openPrintNftModal(nft.id) })
      };
    } else {
      return {
        withdraw: () => handleWithdraw(nft),
        transfer: () => handleTransferNft(nft),
        ...((IS_KIOSK_MODE ? !isCollector && !isCompany : !isCompany) && {
          sell: () => handleListForSale(nft)
        }),
        ...(IS_KIOSK_MODE && { print: () => openPrintNftModal(nft.id) })
      };
    }
  };

  const createNonAMNonCreatorActions = () => {
    if (isSelling) {
      return {
        revoke: () => setRevokeId(nft.id),
        sell: () => handleListForSale(nft),
        withdraw: () => handleWithdraw(nft),
        transfer: () => handleTransferNft(nft),
        ...(IS_KIOSK_MODE && { print: () => openPrintNftModal(nft.id) })
      };
    } else {
      if (isMintConfirmed) {
        return {
          sell: () => handleListForSale(nft),
          withdraw: () => handleWithdraw(nft),
          transfer: () => handleTransferNft(nft),
          ...(IS_KIOSK_MODE && { print: () => openPrintNftModal(nft.id) })
        };
      } else {
        return {
          transfer: () => handleTransferNft(nft),
          ...((IS_KIOSK_MODE ? !isCollector && !isCompany : !isCompany) && {
            sell: () => handleListForSale(nft)
          }),
          ...(IS_KIOSK_MODE && { print: () => openPrintNftModal(nft.id) }),
          ...(isMintOnWithdraw && { withdraw: () => handleWithdraw(nft) })
        };
      }
    }
  };
  // Generate actions based on conditions
  if (isCreator && isOwner) {
    if (IS_FTNFT_AM_MODE) {
      return createAMCreatorActions();
    } else {
      return createNonAMCreatorActions();
    }
  } else if (isOwner && !isCreator) {
    if (IS_FTNFT_AM_MODE) {
      return createAMNonCreatorActions();
    } else {
      return createNonAMNonCreatorActions();
    }
  } else if (!isForMyNftList) {
    return {
      openOriginal: true,
      share: true
    };
  } else {
    return {};
  }
};
